import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Question, QuestionType } from '../types';
import { BUILT_IN_ICONS } from '../data';
import { useFundamentals, getFundamentalName, getHabitName } from '@/hooks/useFundamentals';
import { IconPickerModal } from './IconPickerModal';

interface QuestionCardProps {
  q: Question;
  index: number;
  onChange: (field: keyof Question, value: unknown) => void;
  onRemove: () => void;
}

export function QuestionCard({ q, index, onChange, onRemove }: QuestionCardProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const { data: fundamentals = [] } = useFundamentals();
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const linksToFundamental = (q.type === 'rating' || q.type === 'behaviors') && q.enfoque !== 'colectivo';
  const selectedFund = fundamentals.find(f => f.id === q.fundamental);
  const habits = selectedFund?.dailyHabits ?? [];

  const typeOpts: Array<{ value: QuestionType; label: string }> = q.enfoque === 'colectivo'
    ? [
        { value: 'rating',    label: t('admin.builderTypeRating') },
        { value: 'multiple',  label: t('admin.builderTypeMultiple') },
        { value: 'open',      label: t('admin.builderTypeOpen') },
        { value: 'nps',       label: t('admin.builderTypeNPS') },
      ]
    : [
        { value: 'rating',    label: t('admin.builderTypeRating') },
        { value: 'multiple',  label: t('admin.builderTypeMultiple') },
        { value: 'open',      label: t('admin.builderTypeOpen') },
        { value: 'behaviors', label: t('admin.builderTypeBehaviors') },
      ];

  const iconLabel = q.icon
    ? (BUILT_IN_ICONS.find(b => b.value === q.icon)?.label || t('admin.builderCustomIcon'))
    : t('admin.builderNoIcon');

  return (
    <div className="question-card-builder">
      {showIconPicker && (
        <IconPickerModal
          currentIcon={q.icon}
          onSelect={v => onChange('icon', v)}
          onClose={() => setShowIconPicker(false)}
        />
      )}
      <div
        className="question-card-header"
        style={{ cursor: 'pointer' }}
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="question-card-header-left">
          <i className={`fas fa-chevron-${collapsed ? 'right' : 'down'} qcard-chevron`} />
          <span className="question-number">{t('admin.builderQuestionLabel')} {index + 1}</span>
          {collapsed && q.text && (
            <span className="qcard-collapsed-preview">{q.text}</span>
          )}
        </div>
        <button
          type="button"
          className="btn-icon danger"
          onClick={e => { e.stopPropagation(); onRemove(); }}
          title={t('admin.builderDeleteQuestion')}
        >
          <i className="fas fa-trash" />
        </button>
      </div>

      {!collapsed && (
        <div className="question-card-body">

          {/* Enfoque */}
          <div className="form-group">
            <label>{t('admin.builderApproachLabel')}</label>
            <div className="q-enfoque-toggle">
              <button
                type="button"
                className={`q-enfoque-btn${q.enfoque === 'individual' ? ' active' : ''}`}
                onClick={() => {
                  onChange('enfoque', 'individual');
                  if (q.type === 'nps') onChange('type', 'rating');
                }}
              >
                <i className="fas fa-user-check" />
                <div>
                  <span>{t('admin.builderApproachIndividual')}</span>
                  <small>{t('admin.builderIndividualSub')}</small>
                </div>
              </button>
              <button
                type="button"
                className={`q-enfoque-btn${q.enfoque === 'colectivo' ? ' active' : ''}`}
                onClick={() => {
                  onChange('enfoque', 'colectivo');
                  if (q.type === 'behaviors') onChange('type', 'rating');
                }}
              >
                <i className="fas fa-users" />
                <div>
                  <span>{t('admin.builderApproachCollective')}</span>
                  <small>{t('admin.builderCollectiveSub')}</small>
                </div>
              </button>
            </div>
          </div>

          {/* Modo presentación (solo individual) */}
          {q.enfoque === 'individual' && (
            <div className="form-group">
              <label>{t('admin.builderPresentationMode')}</label>
              <div className="present-mode-toggle">
                <button
                  type="button"
                  className={`present-mode-btn${q.presentMode === 'matrix' ? ' active' : ''}`}
                  onClick={() => onChange('presentMode', 'matrix')}
                >
                  <i className="fas fa-table" />
                  <span>{t('admin.builderMatrix')}</span>
                  <small>{t('admin.builderMatrixSub')}</small>
                </button>
                <button
                  type="button"
                  className={`present-mode-btn${q.presentMode === 'individual' ? ' active' : ''}`}
                  onClick={() => onChange('presentMode', 'individual')}
                >
                  <i className="fas fa-user" />
                  <span>{t('admin.builderIndividualCard')}</span>
                  <small>{t('admin.builderIndividualCardSub')}</small>
                </button>
              </div>
            </div>
          )}

          {/* Tipo */}
          <div className="form-group">
            <label>{t('admin.builderTypeLabel')}</label>
            <select
              className="form-input"
              value={q.type}
              onChange={e => onChange('type', e.target.value as QuestionType)}
            >
              {typeOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Fundamental + CoreBehavior */}
          {linksToFundamental && (
            <>
              <div className="form-group">
                <label>{t('admin.builderFundamentalLabel')} *</label>
                <select
                  className="form-input"
                  value={q.fundamental}
                  onChange={e => { onChange('fundamental', e.target.value); onChange('coreBehavior', ''); }}
                >
                  <option value="">{t('admin.builderSelectFundamental')}</option>
                  {fundamentals.map(f => <option key={f.id} value={f.id}>{getFundamentalName(f, lang)}</option>)}
                </select>
              </div>
              {q.fundamental && (
                <div className="form-group">
                  <label>{t('admin.builderDailyHabitLabel')} *</label>
                  <select
                    className="form-input"
                    value={q.coreBehavior}
                    onChange={e => onChange('coreBehavior', e.target.value)}
                  >
                    <option value="">{t('admin.builderSelectDailyHabit')}</option>
                    {habits.map(b => <option key={b.id} value={b.id}>{getHabitName(b, lang)}</option>)}
                  </select>
                </div>
              )}
              {q.fundamental && q.coreBehavior && (
                <div className="info-banner-small">
                  <i className="fas fa-link" />
                  <p>
                    {t('admin.builderLinkedTo')}{' '}
                    <strong>{selectedFund ? getFundamentalName(selectedFund, lang) : q.fundamental}</strong>
                    {' → '}
                    <strong>{habits.find(b => b.id === q.coreBehavior) ? getHabitName(habits.find(b => b.id === q.coreBehavior)!, lang) : q.coreBehavior}</strong>
                  </p>
                </div>
              )}
            </>
          )}

          {/* Texto */}
          <div className="form-group">
            <label>{t('admin.builderQuestionText')} *</label>
            <input
              type="text"
              className="form-input"
              value={q.text}
              placeholder={t('admin.builderQuestionTextPlaceholder')}
              onChange={e => onChange('text', e.target.value)}
            />
          </div>

          {/* Ícono */}
          <div className="form-group">
            <label>
              {t('admin.builderIconLabel')}{' '}
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                {t('admin.builderOptional')}
              </span>
            </label>
            <div className="icp-selected-row">
              {q.icon
                ? <div className="icp-selected-thumb"><img src={q.icon} alt={t('admin.builderIconLabel')} /></div>
                : <div className="icp-selected-thumb icp-selected-none"><i className="fas fa-ban" /></div>
              }
              <div className="icp-selected-info">
                <span className="icp-selected-name">{iconLabel}</span>
                {q.icon && (
                  <span className="icp-selected-badge">
                    <i className="fas fa-check" /> {t('admin.builderSelectedIcon')}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn-small btn-secondary-sm"
                onClick={() => setShowIconPicker(true)}
              >
                <i className="fas fa-images" />{' '}
                {q.icon ? t('admin.builderChangeIcon') : t('admin.builderSelectIconBtn')}
              </button>
            </div>
          </div>

          {/* Opciones: múltiple O individual+comportamientos */}
          {(q.type === 'multiple' || (q.enfoque === 'individual' && q.type === 'behaviors')) && (
            <div className="form-group">
              <label>{t('admin.builderOptionsLabel')}</label>
              <div className="options-list">
                {q.options.map((opt, oi) => (
                  <div key={oi} className="option-input-group">
                    <span className="option-number">{oi + 1}.</span>
                    <input
                      type="text"
                      className="form-input"
                      value={opt}
                      onChange={e => {
                        const opts = [...q.options];
                        opts[oi] = e.target.value;
                        onChange('options', opts);
                      }}
                    />
                    <button
                      type="button"
                      className="btn-icon danger btn-icon-small"
                      onClick={() => onChange('options', q.options.filter((_, i) => i !== oi))}
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ))}
                {q.includeNoObs !== false ? (
                  <div className="option-input-group option-no-obs">
                    <span className="option-number">
                      <i className="fas fa-eye-slash" style={{ color: '#9ca3af' }} />
                    </span>
                    <input
                      type="text"
                      className="form-input"
                      value={t('admin.builderNoObservable')}
                      disabled
                      style={{ color: 'var(--text-muted)', fontStyle: 'italic', background: 'rgba(0,0,0,0.02)' }}
                    />
                    <button
                      type="button"
                      className="btn-icon btn-icon-small"
                      onClick={() => onChange('includeNoObs', false)}
                      title={t('admin.builderRemoveNoObs')}
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="bld-add-nobs-btn"
                    onClick={() => onChange('includeNoObs', true)}
                  >
                    <i className="fas fa-eye-slash" /> {t('admin.builderAddNoObs')}
                  </button>
                )}
              </div>
              {q.options.length < 4 && (
                <button
                  type="button"
                  className="btn-small btn-secondary-sm"
                  style={{ marginTop: 10 }}
                  onClick={() => onChange('options', [...q.options, `${t('admin.builderOptionDefault')} ${q.options.length + 1}`])}
                >
                  <i className="fas fa-plus" /> {t('admin.builderAddOption')} ({q.options.length}/4)
                </button>
              )}
              {q.options.length >= 4 && (
                <small className="form-hint" style={{ marginTop: 8, display: 'block' }}>
                  {t('admin.builderMaxOptions')}
                </small>
              )}
            </div>
          )}

          {q.type === 'nps' && (
            <div className="info-banner-small" style={{ marginTop: 12 }}>
              <i className="fas fa-chart-line" />
              <p>{t('admin.builderNPSInfo')}</p>
            </div>
          )}
          {q.type === 'open' && (
            <div className="info-banner-small" style={{ marginTop: 12 }}>
              <i className="fas fa-info-circle" />
              <p>{t('admin.builderOpenInfo')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
